import { useMemo } from 'react';

import { CORRIDOR_TYPES, useGetMappingCorridorsQuery } from '@/entities/dashboard/mapping';
import type { CorridorType, MappingCorridor } from '@/entities/dashboard/mapping';
import type { LocationModel } from '@/entities/location';
import { useProjectSiteParams } from '@/shared/lib';

import { createCorridorDisplay, isCorridorDrawable } from '../lib/corridor.utils';
import type { CorridorDisplay } from './types';

interface UseMapCorridorsParams {
  locations: LocationModel[];
  /** Corridor datasets the lit layers ask for — both may be on at once, as in legacy. */
  types: CorridorType[];
  organizationId?: string;
  vehicleCategories?: string[];
  consignmentNumber?: string;
  /** Weeks back the time machine is showing. */
  weeksAgo?: number;
}

interface CorridorFilters {
  organizationId?: string;
  /** Comma-separated category ids — the picker is a multi-select. */
  vehicleCategoryIds?: string;
  consignmentNumber?: string;
  /** Weeks back the time machine is showing; corridors younger than that are dropped. */
  weekNo?: number;
}

/**
 * One corridor dataset, narrowed by all three map filters at once — the way legacy
 * filters them (`MappingBean:661,679,759`).
 */
function useCorridorDataset(type: CorridorType, enabled: boolean, filters: CorridorFilters): MappingCorridor[] {
  const { projectId, siteId } = useProjectSiteParams();

  const { data } = useGetMappingCorridorsQuery(
    { type, projectId, siteId, ...filters },
    { skip: !enabled || !projectId || !siteId }
  );

  return useMemo(() => (enabled ? (data?.features ?? []) : []), [enabled, data]);
}

export function useMapCorridors({
  locations,
  types,
  organizationId,
  vehicleCategories,
  consignmentNumber,
  weeksAgo
}: UseMapCorridorsParams): Record<CorridorType, CorridorDisplay[]> {
  // Empty means "no filter", and RTK Query only strips `undefined` from params.
  const filters = useMemo(
    () => ({
      organizationId: organizationId || undefined,
      vehicleCategoryIds: vehicleCategories?.length ? vehicleCategories.join(',') : undefined,
      consignmentNumber: consignmentNumber || undefined,
      weekNo: weeksAgo || undefined
    }),
    [organizationId, vehicleCategories, consignmentNumber, weeksAgo]
  );

  const inTransit = useCorridorDataset(CORRIDOR_TYPES.IN_TRANSIT, types.includes(CORRIDOR_TYPES.IN_TRANSIT), filters);
  const inHouse = useCorridorDataset(CORRIDOR_TYPES.IN_HOUSE, types.includes(CORRIDOR_TYPES.IN_HOUSE), filters);

  const locationsMap = useMemo(() => new Map(locations.map((location) => [location.id, location])), [locations]);

  const toDisplays = (corridors: MappingCorridor[]): CorridorDisplay[] =>
    corridors
      .map((corridor) => {
        const from = locationsMap.get(corridor.from);
        const to = locationsMap.get(corridor.to);

        return from && to && isCorridorDrawable(from, to) ? createCorridorDisplay(corridor, from, to) : null;
      })
      .filter((display): display is CorridorDisplay => display !== null);

  return useMemo(
    () => ({
      [CORRIDOR_TYPES.IN_TRANSIT]: toDisplays(inTransit),
      [CORRIDOR_TYPES.IN_HOUSE]: toDisplays(inHouse)
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inTransit, inHouse, locationsMap]
  );
}
